// ./src/components/features/Storage/StorageFilterBar/StorageFilterBar.tsx
//
// Controlled filter for the Storage page: by media kind (client-side, from mime)
// and by purpose (server-side — needs the ms_storage `purpose` facet recomposed
// into the gateway). The page owns the value and maps it to the query.

import React from "react";
import { Group, SegmentedControl, Button, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, SelectField } from "@/components/generic";

export type StorageKind = "image" | "video" | "audio" | "document";

export interface StorageFilterValue {
  kind: StorageKind | null;
  purpose: string | null;
}

export const EMPTY_STORAGE_FILTER: StorageFilterValue = { kind: null, purpose: null };

// Mirrors ms_storage FILE_PURPOSES; the roles a file can play.
export const STORAGE_PURPOSES = [
  "avatar",
  "cover",
  "cover_video",
  "background",
  "logo",
  "gallery",
  "flyer",
  "map",
  "document",
  "submission",
  "seo_image",
  "general",
];

const PURPOSE_OPTIONS = STORAGE_PURPOSES;

export interface StorageFilterBarProps {
  value: StorageFilterValue;
  onChange: (value: StorageFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
  /** Stack vertically (mobile filter drawer) vs inline (desktop toolbar). */
  vertical?: boolean;
}

const ANY = "__any__";

export const StorageFilterBar: React.FC<StorageFilterBarProps> = React.memo(
  ({ value, onChange, disabled, showClear = true, vertical = false }) => {
    const { t } = useTranslation(["common"]);

    const hasFilter = value.kind !== null || value.purpose !== null;

    const kindControl = (
      <SegmentedControl
        size="md"
        disabled={disabled}
        value={value.kind ?? ANY}
        onChange={(v) => onChange({ ...value, kind: v === ANY ? null : (v as StorageKind) })}
        data={[
          { value: ANY, label: t("common:storage.kindAll", { defaultValue: "All" }) },
          { value: "image", label: t("common:storage.kindImage", { defaultValue: "Images" }) },
          { value: "video", label: t("common:storage.kindVideo", { defaultValue: "Videos" }) },
          { value: "audio", label: t("common:storage.kindAudio", { defaultValue: "Audio" }) },
          { value: "document", label: t("common:storage.kindDocument", { defaultValue: "Docs" }) },
        ]}
      />
    );

    const purposeControl = (
      <SelectField
        size="md"
        disabled={disabled}
        clearable
        placeholder={t("common:storage.purposeAll", { defaultValue: "Any purpose" })}
        value={value.purpose}
        onChange={(_name, v) => onChange({ ...value, purpose: v })}
        data={PURPOSE_OPTIONS.map((p) => ({
          value: p,
          label: t(`common:storage.purpose.${p}`, { defaultValue: p.replace(/_/g, " ") }),
        }))}
        style={{ minWidth: 180 }}
      />
    );

    const clearButton = showClear && hasFilter && (
      <Button
        variant="subtle"
        size="md"
        color="gray"
        leftSection={<AppIcon iconName="X" size={16} />}
        onClick={() => onChange(EMPTY_STORAGE_FILTER)}
      >
        {t("common:buttons.clear", { defaultValue: "Clear" })}
      </Button>
    );

    if (vertical) {
      return (
        <Stack gap="sm">
          {kindControl}
          {purposeControl}
          {clearButton}
        </Stack>
      );
    }

    return (
      <Group gap="sm" wrap="wrap">
        {kindControl}
        {purposeControl}
        {clearButton}
      </Group>
    );
  }
);

StorageFilterBar.displayName = "StorageFilterBar";
